import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useFormContext, useFieldArray, Controller } from "react-hook-form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
// import { useMapboxSuggestions } from "@/hooks/mapbox";
// import { UseFormSetValue, FieldValues } from "react-hook-form";
import { AddressAutofill } from "@mapbox/search-js-react";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectItem,
  SelectContent,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { FLOOR_LEVELS, US_STATES } from "@/constants";
import { useSelector } from "react-redux";
import { getSelectedMover } from "@/store/mover/slice";
import { AddressType, Mover } from "@/types";
import DriveTimes from "@/components/DriveTimes";
import { DriveTimeSegment } from "@/types";
import { PlusIcon, Trash2Icon } from "lucide-react";
import { addressBlank } from "@/lib/utils";

const MAPBOX_ACCESS_TOKEN =
  "pk.eyJ1IjoidjFsYWJzIiwiYSI6ImNtMnc1aDFoZzAzMmQya293MnI2YTN1OW0ifQ.GMteqvN0XlFnjpuRFhCuGw";

interface AddressData {
  street: string;
  city: string;
  state: string;
  zipcode: string;
  name: string;
  number: string;
  floor: number;
  elevator: boolean;
  latitude?: number;
  longitude?: number;
}

interface AddressProps {
  name: string;
  mover: Mover | null;
  label?: string;
  address?: AddressData;
  onRemove?: () => void;
  showRemoveButton?: boolean;
}

const Address: React.FC<AddressProps> = ({
  name,
  mover,
  label,
  address,
  onRemove,
  showRemoveButton = false,
}) => {
  const {
    control,
    setValue,
    formState: { errors },
  } = useFormContext();
  // const [useManualInput, setUseManualInput] = useState(false);
  // const { suggestions, getSuggestions, getAddressDetails } = useMapboxSuggestions();

  useEffect(() => {
    if (address) {
      // setValue(`${name}.street`, address.street);
      // setValue(`${name}.city`, address.city);
      // setValue(`${name}.state`, address.state);
      // setValue(`${name}.zipcode`, address.zipcode);
      // If we have an existing address, default to manual input
      // setUseManualInput(true);
    }
  }, [address, name, setValue]);

  const handleAddressSelect = (result: any) => {
    const { features } = result;
    if (features && features.length > 0) {
      const { properties, geometry } = features[0];
      setValue(`${name}.label`, name);
      setValue(`${name}.street`, properties.address || "");
      setValue(
        `${name}.city`,
        properties.place || properties.context?.place?.[0]?.text || ""
      );
      setValue(
        `${name}.state`,
        properties.region_code || properties.context?.region?.[0]?.text || ""
      );
      setValue(`${name}.zipcode`, properties.postcode || "");
      setValue(`${name}.latitude`, geometry.coordinates[1]);
      setValue(`${name}.longitude`, geometry.coordinates[0]);
    }
  };

  // const getFormattedAddress = () => {
  //   if (address) {
  //     return `${address.street}, ${address.city}, ${address.state} ${address.zipcode}`;
  //   }
  //   return "";
  // };

  return (
    <div className="space-y-2">
      <>
        <div className="flex flex-row justify-start items-baseline gap-4">
          <FormLabel className="flex flex-row items-center gap-2">
            <span className="font-bold">{label}</span>
            {name === "destination" && (
              <span className="text-sm text-muted-foreground">
                (leave empty if there's no destination)
              </span>
            )}
            {name === "origin" && errors.origin && (
              <FormMessage>{errors.origin.message?.toString()}</FormMessage>
            )}
            {name === "destination" && errors.destination && (
              <FormMessage>
                {errors.destination.message?.toString()}
              </FormMessage>
            )}
          </FormLabel>
          {/* add a remove button  */}
          {showRemoveButton && (
            <Button
              variant="link"
              className="text-destructive p-0 flex flex-row items-center gap-1"
              onClick={onRemove}
            >
              <Trash2Icon className="w-3 h-3" />
              Remove
            </Button>
          )}
          {/* <a
            className="text-sm font-semibold cursor-pointer "
            onClick={() => setUseManualInput(true)}
          >
            Type the full address
          </a> */}
        </div>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col lg:flex-row items-start gap-2 lg:grid lg:grid-cols-12">
            <div className="w-full lg:col-span-5">
              {/* mapbox/search-js-react doesn't have a type definition */}
              {/* <Label>Street</Label> */}
              {/* @ts-expect-error */}
              <AddressAutofill
                accessToken={MAPBOX_ACCESS_TOKEN}
                onRetrieve={handleAddressSelect}
                options={{
                  proximity:
                    mover?.latitude && mover?.longitude
                      ? [mover.longitude, mover.latitude]
                      : "ip",
                }}
              >
                <Controller
                  name={`${name}.street`}
                  control={control}
                  defaultValue={address?.street || ""}
                  render={({ field }) => (
                    <Input
                      {...field}
                      autoComplete="street-address"
                      placeholder="Type street address"
                    />
                  )}
                />
              </AddressAutofill>
            </div>
            <div className="w-full lg:col-span-3">
              {/* <Label>City</Label> */}
              <Controller
                name={`${name}.city`}
                control={control}
                defaultValue={address?.city || ""}
                render={({ field }) => (
                  <Input autoComplete="off" placeholder="City" {...field} />
                )}
              />
            </div>
            <div className="w-full lg:col-span-2">
              {/* <Label>State</Label> */}
              <Controller
                name={`${name}.state`}
                control={control}
                defaultValue={address?.state?.toUpperCase() || ""}
                render={({ field }) => (
                  <Select
                    value={field.value || ""}
                    onValueChange={field.onChange}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="State" />
                    </SelectTrigger>
                    <SelectContent>
                      {US_STATES.map((state) => (
                        <SelectItem key={state.value} value={state.value}>
                          {state.value}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
            <div className="w-full lg:col-span-2">
              {/* <Label>Zipcode</Label> */}
              <Controller
                name={`${name}.zipcode`}
                control={control}
                defaultValue={address?.zipcode || ""}
                render={({ field }) => (
                  <Input
                    placeholder="Zipcode"
                    maxLength={5}
                    autoComplete="off"
                    {...field}
                  />
                )}
              />
            </div>
          </div>
          <div className="flex flex-col md:flex-row gap-4">
            <FormField
              name={`${name}.name`}
              control={control}
              render={({ field }) => (
                <FormItem className="flex-grow">
                  {/* <Label>Name</Label> */}
                  <FormControl>
                    <Input
                      className="w-full"
                      type="text"
                      placeholder="Apt/Unit Name"
                      {...field}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              name={`${name}.number`}
              control={control}
              render={({ field }) => (
                <FormItem>
                  {/* <Label>Number</Label> */}
                  <FormControl>
                    <Input placeholder="Number" className="w-20" {...field} />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name={`${name}.floor`}
              render={({ field }) => (
                <FormItem className="flex-grow">
                  {/* <Label>Access</Label> */}
                  <FormControl>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value?.toString() || ""}
                      defaultValue=""
                    >
                      <SelectTrigger className="w-48">
                        <SelectValue placeholder="Select Access" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">{FLOOR_LEVELS[0]}</SelectItem>
                        <SelectItem value="1">{FLOOR_LEVELS[1]}</SelectItem>
                        <SelectItem value="2">{FLOOR_LEVELS[2]}</SelectItem>
                        <SelectItem value="3">{FLOOR_LEVELS[3]}</SelectItem>
                        <SelectItem value="4">{FLOOR_LEVELS[4]}</SelectItem>
                        <SelectItem value="99">{FLOOR_LEVELS[99]}</SelectItem>
                      </SelectContent>
                    </Select>
                  </FormControl>
                </FormItem>
              )}
            />
            <div className="flex flex-row gap-4 mt-2">
              {/* {parseInt(watch(`${name}.floor`)) > 0 && (
                <FormField
                  control={control}
                  name={`${name}.elevator`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Elevator?</FormLabel>
                      <FormControl>
                        <Select
                          value={field.value?.toString()}
                          onValueChange={(value) =>
                            field.onChange(value === "true")
                          }
                        >
                          <SelectTrigger className="w-20">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="true">Yes</SelectItem>
                            <SelectItem value="false">No</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormControl>
                    </FormItem>
                  )}
                />
              )} */}
            </div>
          </div>
        </div>
      </>
    </div>
  );
};

const FormattedAddress: React.FC<{
  name: string;
  mover: Mover | null;
  label: string;
  address?: AddressData;
  onRemove?: () => void;
  showRemoveButton?: boolean;
}> = ({ name, mover, label, address, onRemove, showRemoveButton }) => {
  const [isEditing, setIsEditing] = useState(true);

  const formattedAddress = address
    ? `${address.street}, ${address.city}, ${address.state}`
    : null;

  if (isEditing) {
    return (
      <>
        {/* <div className="flex justify-start items-center gap-4">
          <div>
            <h3 className="font-semibold">{label}</h3>
            <p>{formattedAddress}</p>
          </div>
          <Button
            type="button"
            variant="link"
            className="text-black hover:text-black"
            onClick={() => setIsEditing(!isEditing)}
          >
            {isEditing ? "Close" : "Click to add/edit address"}
          </Button>
        </div> */}
        {isEditing && (
          <Card className="border-none rounded-none shadow-none">
            <CardContent className="p-0">
              <Address
                mover={mover}
                name={name}
                label={label}
                address={address}
                onRemove={onRemove}
                showRemoveButton={showRemoveButton}
              />
            </CardContent>
          </Card>
        )}
      </>
    );
  }

  return (
    <>
      <Card>
        <CardContent className="p-4">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="font-semibold">{label}</h3>
              <p>{formattedAddress}</p>
            </div>
            <Button variant="outline" onClick={() => setIsEditing(true)}>
              Edit
            </Button>
          </div>
        </CardContent>
      </Card>
    </>
  );
};

export const Addresses: React.FC = () => {
  const selectedMover = useSelector(getSelectedMover);
  const { control, watch, setValue } = useFormContext();
  const { fields, append } = useFieldArray({
    control,
    name: "stops",
  });
  const stops = watch("stops");

  const handleDriveTimeUpdate = useCallback(
    (driveTimeData: DriveTimeSegment[]) => {
      setValue("waypoints_info", driveTimeData);
    },
    [setValue]
  );

  const waypoints = watch([
    "origin",
    ...fields.map((_, index) => `stops.${index}`),
    "destination",
  ]);

  const waypointsWithMoverLocation = useMemo(() => {
    if (!selectedMover) {
      return [];
    }

    const origin = watch("origin");
    const stops = watch("stops") || [];
    const destination = watch("destination");

    // if not even one address is filled out, no need to calculate drive times
    if (addressBlank(origin)) {
      return [];
    }

    const moverWaypoint = {
      label: "Truck Parking",
      latitude: selectedMover.latitude,
      longitude: selectedMover.longitude,
    };

    return [
      moverWaypoint,
      { ...origin, label: "Origin" },
      ...stops.map((stop: AddressType, index: number) => ({
        ...stop,
        label: `Stop #${index + 1}`,
      })),
      { ...destination, label: "Destination" },
      moverWaypoint,
    ];
  }, [selectedMover, waypoints]);

  useEffect(() => {
    // Initialize additional stops if provided
    if (stops.length > 0) {
      stops.forEach((stop: AddressType, index: number) => {
        if (index < fields.length) {
          // Update existing field
          setValue(`stops.${index}`, stop);
        }
      });
    }
  }, [stops, append, setValue, fields.length]);

  return (
    <div className="relative">
      <div className="grid grid-cols-12 gap-12">
        <div className="col-span-7">
          <div className="w-full flex flex-col gap-6">
            <div className="w-full">
              <FormattedAddress
                mover={selectedMover}
                name="origin"
                label="Origin"
              />
            </div>
            {fields.map((field, index) => {
              if (stops[index]._destroy) {
                return null;
              }
              return (
                <div className="w-full" key={field.id}>
                  <FormattedAddress
                    mover={selectedMover}
                    label={`Stop #${index + 1}`}
                    name={`stops.${index}`}
                    address={stops[index]}
                    onRemove={() => {
                      setValue(`stops.${index}`, {
                        ...stops[index],
                        _destroy: true,
                      });
                    }}
                    showRemoveButton
                  />
                </div>
              );
            })}
            <div>
              <Button
                type="button"
                variant="link"
                className="text-black underline underline-offset-4 justify-start pl-0 flex flex-row items-center gap-0.5 w-auto"
                onClick={() =>
                  append({ street: "", city: "", state: "", zipcode: "" })
                }
              >
                <PlusIcon className="w-4 h-4" />
                Add a Stop
              </Button>
            </div>
            <div className="w-full">
              <FormattedAddress
                mover={selectedMover}
                name="destination"
                label="Final Destination / Drop-off"
                // onRemove={() => remove(fields.length - 1)}
                // showRemoveButton
              />
            </div>
            <Controller
              control={control}
              name="waypoints_info"
              defaultValue={[]}
              render={({ field }) => (
                <input
                  type="hidden"
                  {...field}
                  value={JSON.stringify(field.value)}
                />
              )}
            />
          </div>
        </div>
        <div className="col-span-5">
          {waypointsWithMoverLocation.length > 0 && (
            <DriveTimes
              waypoints={waypointsWithMoverLocation}
              onChange={handleDriveTimeUpdate}
            />
          )}
        </div>
      </div>
    </div>
  );
};
